import { Extensions, JSONContent, wrappingInputRule } from '@tiptap/core';
import Blockquote from '@tiptap/extension-blockquote';
import Bold from '@tiptap/extension-bold';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import Heading from '@tiptap/extension-heading';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import { BulletList, ListItem, OrderedList, orderedListInputRegex } from '@tiptap/extension-list';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';

// Die Allow-List fuer Ankuendigungs-Bodies (Client-Dok §8.1). Serverseitig durchgesetzt vom
// RichTextValidator; wer sie hier aendert, muss den Validator und §8.1 im selben Zug
// nachziehen — das Dokument sagt das ausdruecklich.
//
// Diese Datei ist die EINZIGE Quelle: Editor (announcement-body-input), Validator
// (announcement-form) und Renderer (Stream) benutzen sie gemeinsam. Ein Renderer, der mehr
// kann als der Server erlaubt, verdeckt Fehler statt sie zu zeigen (Client-Dok §11).

/** Nur diese drei Schemata sind als Linkziel zulaessig (Client-Dok §8.1). */
const ALLOWED_HREF = /^(https?:|mailto:)/i;

/**
 * Der Server lehnt einen Body ab, dessen Klartext-Aequivalent diese Grenze ueberschreitet.
 * Serverseitig konfigurierbar, Default fuer alle Umgebungen gleich.
 */
export const ANNOUNCEMENT_BODY_MAX_CHARS = 10_000;

/**
 * BEWUSST aufgezaehlt statt `StarterKit.configure({ ... false })`.
 *
 * StarterKit ist eine wachsende Menge: kaeme in einer kuenftigen Version ein neuer,
 * standardmaessig aktiver Knoten hinzu, verletzten wir den Vertrag, ohne eine Zeile geaendert
 * zu haben — und merkten es erst, wenn ein Autor eine Ablehnung vom Server bekommt.
 *
 * Zwei Regeln aus §8.1 sind Strukturregeln und werden als Schema durchgesetzt, nicht als
 * Werkzeugleiste. Das deckt zugleich das EINFUEGEN ab: ProseMirror bildet eingefuegten Inhalt
 * zwingend auf das Schema ab, eine Word-Tabelle faellt also auf Absaetze zurueck. Kein
 * eigener Paste-Handler noetig.
 */
export const ANNOUNCEMENT_RICH_TEXT_EXTENSIONS: Extensions = [
  Document,
  Paragraph,
  Text,
  // Ebene 1 ist der Nachrichtentitel und gehoert nicht in den Body.
  Heading.configure({ levels: [2, 3] }),
  // TipTap-Default waere 'block+' — §8.1: „enthaelt nur paragraph".
  Blockquote.extend({ content: 'paragraph+' }),
  BulletList,
  // Der Server lehnt einen Body ab, dessen orderedList-Knoten start !== 1 traegt
  // (RichTextValidator.java: „Nummerierung darf nicht bei X beginnen, erlaubt ist nur 1"). Ohne
  // diese Sperre entsteht start!=1 auf zwei Wegen, die BEIDE gekappt werden muessen:
  //
  // 1) Tippen von "3. " am Zeilenanfang loest OrderedLists eigene Input-Rule aus. Die baut den
  //    Knoten programmatisch mit attrs: { start: 3 } — parseHTML greift hier NICHT, das ist
  //    ausschliesslich der DOM-Parse-Pfad (siehe 2). ProseMirrors computeAttrs uebernimmt einen
  //    explizit gegebenen Attributwert unveraendert, ungeprueft. Deshalb addInputRules() unten:
  //    ohne getAttributes faellt jede Instanz auf den Schema-Default (1) zurueck, egal welche
  //    Zahl vor dem Punkt stand.
  // 2) Einfuegen von <ol start="3"> laeuft durch DOMParser.fromSchema — dort greift parseHTML.
  //    addAttributes() unten kappt diesen Weg.
  //
  // `type` (<ol type="a">) faellt mit weg: der Server akzeptiert es, verwirft es aber beim
  // Rendern stillschweigend — exakt das will der Server mit seiner start-Regel vermeiden, siehe
  // RichTextValidator.java:96-105.
  OrderedList.extend({
    addAttributes() {
      return { start: { default: 1, parseHTML: () => 1, renderHTML: () => ({}) } };
    },
    addInputRules() {
      return [wrappingInputRule({ find: orderedListInputRegex, type: this.type })];
    }
  }),
  // TipTap-Default waere 'paragraph block*' — genau darueber entstehen verschachtelte Listen.
  // 'paragraph' macht Verschachtelung strukturell unmoeglich (§8.1: „nur genau einen
  // paragraph"). Verschachtelte Listen braeuchten im Mail-HTML eine Tabellenebene pro Stufe,
  // die Outlook unzuverlaessig rendert.
  ListItem.extend({ content: 'paragraph' }),
  HardBreak,
  Bold,
  Italic,
  // ACHTUNG: die Option `protocols` reicht NICHT — sie steuert das Autolinking, nicht das
  // Parsen; mit ihr allein ueberlebt ein <a href="ftp://x"> das Einfuegen samt Mark.
  // isAllowedUri greift dagegen im Parse-Pfad.
  Link.configure({
    openOnClick: false,
    autolink: false,
    isAllowedUri: (url: string) => ALLOWED_HREF.test(url)
  })
];

/**
 * Zeichenzahl des Klartext-Aequivalents. Quelle fuer BEIDES: die Anzeige im Editor und die
 * Sperre im Validator — zwei Implementierungen wuerden leicht verschieden zaehlen, und ein
 * „9.998 / 10.000" neben einem gesperrten Knopf waere die Folge.
 *
 * Arbeitet auf dem Dokument, nicht auf einer Editor-Instanz: der Validator sieht den
 * Formularwert, nicht den Editor.
 */
export function richTextLength(doc: JSONContent | null | undefined): number {
  if (!doc) {
    return 0;
  }
  let length = (doc.text ?? '').length;
  for (const child of doc.content ?? []) {
    length += richTextLength(child);
  }
  return length;
}

/**
 * Leer im fachlichen Sinn. Der Editor liefert nie `null`, sondern ein Dokument mit einem
 * leeren Absatz — beides muss als „nichts geschrieben" gelten.
 */
export function isEmptyRichText(doc: JSONContent | null | undefined): boolean {
  return richTextLength(doc) === 0;
}
